import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2, Loader2, Lock, MessageSquarePlus, Users } from 'lucide-react'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import { useCommunity } from '@/lib/CommunityContext'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FilterPills } from '@/components/ui/filter-pills'
import type { MoodPulseCell, MoodPulsePending, MoodPulseRound, MoodTeamTrend, MoodTrend } from '@/types/surveys'
import { MOOD_FACES, MOOD_VALUES, MoodFaces, faceOf } from './moodFaces'
import { errMessage, errStatus } from './surveyUtils'

/* One color per face, worst to best: literal classes so Tailwind ships them. */
const FACE_BAR = ['bg-red-400', 'bg-orange-300', 'bg-muted-foreground/40', 'bg-lime-400', 'bg-green-500']

const useWeekLabel = () => {
  const { i18n } = useTranslation('moodPulse')
  const { community } = useCommunity()
  return (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { timeZone: community?.timezone || undefined, day: 'numeric', month: 'short' })
}

/** The five faces' shares as one stacked bar (the counts on hover and for screen readers). */
const Distribution = ({ cell }: { cell: MoodPulseCell }) => {
  const { t } = useTranslation('moodPulse')
  const dist = cell.distribution ?? []
  const total = dist.reduce((n, d) => n + d.count, 0)
  if (!total) return null
  const label = dist.map((d) => `${MOOD_FACES[d.value - 1]} ${t(`faces.${d.value}`)}: ${d.count}`).join(' · ')
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted" role="img" aria-label={label} title={label}>
      {dist.filter((d) => d.count > 0).map((d) => (
        <span key={d.value} className={FACE_BAR[d.value - 1]} style={{ width: `${(d.count / total) * 100}%` }} />
      ))}
    </div>
  )
}

/** One week of one slice: the face of the average, the average, the bar and the count, or "poucas respostas". */
const RoundRow = ({ round, cell }: { round: MoodPulseRound; cell: MoodPulseCell }) => {
  const { t, i18n } = useTranslation('moodPulse')
  const week = useWeekLabel()
  const num = (v: number) => v.toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  const hasNumber = !cell.fewResponses && cell.average !== null
  return (
    <li className="flex items-center gap-3 py-3" data-testid="mood-round">
      <div className="w-20 flex-shrink-0 sm:w-28">
        <p className="text-sm font-medium text-foreground">{t('trend.week', { date: week(round.openedAt) })}</p>
        {round.status === 'open' && <Badge variant="info" className="mt-0.5">{t('trend.open')}</Badge>}
      </div>
      {hasNumber ? (
        <>
          <span className="text-2xl leading-none" aria-hidden>{faceOf(cell.average as number)}</span>
          <span className="w-10 flex-shrink-0 text-base font-semibold tabular-nums text-foreground">{num(cell.average as number)}</span>
          <div className="min-w-0 flex-1 space-y-1">
            <Distribution cell={cell} />
            <p className="text-xs text-muted-foreground">{t('trend.responses', { count: cell.responseCount ?? 0 })}</p>
          </div>
        </>
      ) : (
        <p className="flex min-w-0 flex-1 items-center gap-1.5 text-sm text-muted-foreground" data-testid="mood-few">
          <Lock className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
          {round.status === 'open' ? t('trend.openRound') : t('trend.few')}
        </p>
      )}
    </li>
  )
}

const ListSkeleton = () => (
  <div className="animate-pulse space-y-3 rounded-xl border border-border bg-surface p-4">
    {[0, 1, 2].map((i) => <div key={i} className="h-10 rounded bg-muted" />)}
  </div>
)

/**
 * F-241, owner and admin: the weekly mood of the community and of each group
 * (the group as it was when the round opened). Slices under the anonymity
 * minimum say "poucas respostas", never the number.
 */
export const MoodTrendView = ({ surveyId }: { surveyId: string }) => {
  const { t } = useTranslation('moodPulse')
  const [data, setData] = React.useState<MoodTrend | null>(null)
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading')
  const [slice, setSlice] = React.useState('community')

  const load = React.useCallback(async () => {
    setState('loading')
    try {
      setData(await sdk.surveys.moodTrend(surveyId))
      setState('ready')
    } catch {
      setState('error')
    }
  }, [surveyId])
  React.useEffect(() => { void load() }, [load])

  if (state === 'loading') return <ListSkeleton />
  if (state === 'error' || !data) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{t('trend.loadError')}</p>
        <Button variant="outline" size="sm" onClick={() => void load()}>{t('retry')}</Button>
      </div>
    )
  }
  /* Every group seen in any round, in name order; null is "no group". */
  const groups = new Map<string, string>()
  data.rounds.forEach((r) => r.groups.forEach((g) => groups.set(g.groupId ?? 'none', g.groupId ? g.groupName ?? '' : t('trend.noGroup'))))
  const options = [
    { value: 'community', label: t('trend.community') },
    ...[...groups.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => ({ value, label }))
  ]
  const rounds = [...data.rounds].reverse()
  const cellOf = (r: MoodTrend['rounds'][number]): MoodPulseCell => {
    if (slice === 'community') return r.community
    const g = r.groups.find((x) => (x.groupId ?? 'none') === slice)
    /* A group with no one in this round: nothing to show, same as too few. */
    return g ?? { fewResponses: true, responseCount: null, average: null, distribution: null }
  }
  return (
    <section className="space-y-3" data-testid="mood-trend">
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-foreground">{t('trend.title')}</h2>
        <p className="text-sm text-muted-foreground">
          {data.anonymity === 'ANONYMOUS'
            ? t('trend.anonymousHint', { community: data.minimumResponses.community ?? 0, segment: data.minimumResponses.segment })
            : t('trend.identifiedHint')}
        </p>
      </div>
      {options.length > 1 && (
        <FilterPills aria-label={t('trend.sliceLabel')} value={slice} onChange={setSlice} options={options} max={6} data-testid="mood-trend-slice" />
      )}
      {rounds.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border bg-surface p-6 text-center text-sm text-muted-foreground">{t('trend.empty')}</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-border bg-surface px-4">
          {rounds.map((r) => <RoundRow key={r.surveyId} round={r} cell={cellOf(r)} />)}
        </ul>
      )}
      <Legend />
    </section>
  )
}

const Legend = () => {
  const { t } = useTranslation('moodPulse')
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground" aria-label={t('trend.legend')}>
      {MOOD_VALUES.map((v) => (
        <li key={v} className="flex items-center gap-1"><span className={cn('h-2 w-2 rounded-full', FACE_BAR[v - 1])} />{MOOD_FACES[v - 1]} {t(`faces.${v}`)}</li>
      ))}
    </ul>
  )
}

/**
 * F-241 3: "Como você está?" on the Home, for an open round still unanswered.
 * One tap on a face sends it (with the comment, if typed before); then the
 * card thanks and goes away until the next round.
 */
const PendingCard = ({ pending, onDone }: { pending: MoodPulsePending; onDone: () => void }) => {
  const { t } = useTranslation('moodPulse')
  const [comment, setComment] = React.useState('')
  const [commentOpen, setCommentOpen] = React.useState(false)
  const [sending, setSending] = React.useState<number | null>(null)
  const [sent, setSent] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const question = pending.sections.flatMap((s) => s.questions).find((q) => q.id === pending.moodQuestionId)

  const send = async (value: number) => {
    setSending(value)
    setError(null)
    try {
      const text = comment.trim()
      await sdk.surveys.respond(pending.id, [
        { questionId: pending.moodQuestionId, number: value },
        ...(pending.commentQuestionId && text ? [{ questionId: pending.commentQuestionId, text }] : [])
      ])
      setSent(true)
      window.setTimeout(onDone, 2500)
    } catch (e) {
      /* Already answered (409) or the round closed (422): nothing to do here any more. */
      const status = errStatus(e)
      if (status === 409 || status === 422) { onDone(); return }
      setError(errMessage(e, t('card.error')))
    } finally {
      setSending(null)
    }
  }

  if (sent) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800" role="status" data-testid="mood-card-thanks">
        <CheckCircle2 className="h-5 w-5 flex-shrink-0" />{t('card.thanks')}
      </div>
    )
  }
  return (
    <section className="rounded-xl border border-border bg-surface p-4 shadow-sm" aria-labelledby="mood-card-title" data-testid="mood-card">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="mood-card-title" className="text-base font-semibold text-foreground">{t('card.title')}</h2>
          <p className="text-sm text-muted-foreground">{question?.title || t('card.question')}</p>
        </div>
        {pending.anonymity === 'ANONYMOUS' && (
          <span className="flex flex-shrink-0 items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" />{t('card.anonymous')}</span>
        )}
      </div>
      <MoodFaces value={sending} onChange={(v) => void send(v)} disabled={sending !== null} size="lg" labelledBy="mood-card-title" />
      {pending.commentQuestionId && (commentOpen ? (
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} maxLength={5000} autoFocus
          placeholder={t('card.commentPlaceholder')} aria-label={t('card.comment')} data-testid="mood-card-comment"
          className="mt-3 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30" />
      ) : (
        <button type="button" onClick={() => setCommentOpen(true)} className="mt-3 flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
          <MessageSquarePlus className="h-4 w-4" />{t('card.addComment')}
        </button>
      ))}
      <p className="mt-2 text-xs text-muted-foreground">{commentOpen ? t('card.hintWithComment') : t('card.hint')}</p>
      {sending !== null && <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />{t('card.sending')}</p>}
      {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
    </section>
  )
}

/** F-241 5: the leader's own team, by week. Never anyone's answer. */
const TeamCard = ({ data }: { data: MoodTeamTrend }) => {
  const { t } = useTranslation('moodPulse')
  return (
    <section className="rounded-xl border border-border bg-surface p-4 shadow-sm" data-testid="mood-team">
      <h2 className="flex items-center gap-2 text-base font-semibold text-foreground"><Users className="h-4 w-4 text-muted-foreground" />{t('team.title')}</h2>
      <p className="text-sm text-muted-foreground">{t('team.hint', { count: data.minimumResponses })}</p>
      {data.series.map((s) => (
        <div key={s.surveyId} className="mt-2">
          {data.series.length > 1 && <h3 className="text-sm font-medium text-foreground-secondary">{s.title}</h3>}
          {s.rounds.length === 0 ? (
            <p className="py-3 text-sm text-muted-foreground">{t('trend.empty')}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {[...s.rounds].reverse().slice(0, 6).map((r) => <RoundRow key={r.surveyId} round={r} cell={r} />)}
            </ul>
          )}
        </div>
      ))}
    </section>
  )
}

/**
 * F-241 on the Home: the open round to answer and, for whoever leads someone
 * in the org chart, the team's trend. Nothing at all when neither applies.
 */
export const MoodPulseHome = () => {
  const [pending, setPending] = React.useState<MoodPulsePending | null>(null)
  const [team, setTeam] = React.useState<MoodTeamTrend | null>(null)

  React.useEffect(() => {
    let alive = true
    sdk.surveys.moodPulsePending().then((p) => { if (alive) setPending(p) }).catch(() => { /* the Home works without the card */ })
    /* Only a leader asks for the team (anyone else gets a 403 and sees nothing). */
    sdk.hierarchy.me()
      .then((me) => (me.isLeader ? sdk.surveys.moodPulseMyTeam() : null))
      .then((r) => { if (alive && r && r.series.length > 0) setTeam(r) })
      .catch(() => { /* not a leader, or no mood pulse */ })
    return () => { alive = false }
  }, [])

  if (!pending && !team) return null
  return (
    <div className={cn('mx-auto grid max-w-7xl gap-3 px-4 pt-4 sm:px-6', pending && team && 'lg:grid-cols-2')} data-testid="mood-home">
      {pending && <PendingCard pending={pending} onDone={() => setPending(null)} />}
      {team && <TeamCard data={team} />}
    </div>
  )
}
